import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSeo } from "@/lib/site";
import { api, absolutizeHtml, fileUrl, formatDate } from "@/lib/api";
import { MaskedLines, FadeIn } from "@/components/site/Motion";
import { CtaBand, CtaButton, BookMeeting } from "@/components/site/Blocks";

const Article = ({ p }) => {
  useSeo(`${p.meta_title || p.title} | Perfect Digitals`, p.meta_description || p.excerpt || "Search insights from Perfect Digitals.", true);
  return (
    <article data-testid="insight-article">
      <header className="grain relative overflow-hidden bg-ink pb-16 pt-36 text-white sm:pt-44">
        <div aria-hidden="true" className="absolute right-[-10%] top-[-20%] h-[560px] w-[560px] rounded-full bg-violet-700/25 blur-[140px]" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
          <FadeIn><Link to="/insights" data-testid="insight-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-violet-300 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> All insights</Link></FadeIn>
          <div data-no-translate><MaskedLines as="h1" lines={[p.title]} testid="insight-title" className="mt-8 font-display text-4xl font-medium leading-[1.02] sm:text-5xl lg:text-6xl" /></div>
          <FadeIn delay={0.4}>
            {p.excerpt && <p data-no-translate className="mt-6 font-serif-i text-2xl leading-snug text-violet-200">{p.excerpt}</p>}
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Perfect Digitals · {formatDate(p.published_at)}</p>
          </FadeIn>
        </div>
      </header>
      {p.cover_image && (
        <div className="bg-ink">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="relative -mb-24 aspect-[16/9] overflow-hidden rounded-[28px] sm:-mb-32">
              <img src={fileUrl(p.cover_image)} alt={p.title} data-no-translate className="h-full w-full object-cover" data-testid="insight-cover" />
            </div>
          </div>
        </div>
      )}
      <div className={`bg-[#FAFAFC] pb-24 ${p.cover_image ? "pt-36 sm:pt-44" : "pt-16"}`}>
        <div data-no-translate className="prose-pd mx-auto max-w-3xl px-5 sm:px-8" data-testid="insight-content" dangerouslySetInnerHTML={{ __html: absolutizeHtml(p.content_html) }} />
      </div>
    </article>
  );
};

export default function InsightPost() {
  const { slug } = useParams();
  const [state, setState] = useState({ post: null, missing: false });
  useEffect(() => {
    let alive = true;
    setState({ post: null, missing: false });
    api.get(`/posts/${slug}`).then((r) => alive && setState({ post: r.data, missing: false })).catch(() => alive && setState({ post: null, missing: true }));
    return () => { alive = false; };
  }, [slug]);

  if (state.missing) {
    return (
      <div className="grain flex min-h-[80vh] flex-col items-center justify-center bg-ink px-5 pt-24 text-center text-white" data-testid="insight-not-found">
        <h1 className="font-display text-4xl">Article not found</h1>
        <p className="mt-3 text-slate-300">It may have been moved or unpublished.</p>
        <div className="mt-8"><CtaButton label="Back to Insights" to="/insights" testid="insight-not-found-back" /></div>
      </div>
    );
  }
  if (!state.post) return <div className="min-h-screen bg-ink" />;
  return (
    <>
      <Article p={state.post} />
      <CtaBand
        title={["Let's Talk About", <span className="font-serif-i text-gradient">Your Search</span>, "Visibility"]}
        paras={["Book a meeting with our team or get in touch to discuss your visibility across Google and AI search."]}
        ctas={<><BookMeeting testid="insight-book-meeting-cta" /><CtaButton label="Contact Us" variant="ghost" testid="insight-specialist-cta" /></>}
      />
    </>
  );
}
